import { isDoorUnitCurved } from "building-generation/elements/doors/doorUtils";
import type { Building } from "building-types/building/buildingTypes";
import type { DoorUnit } from "building-types/doors/doorTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import { allFaceCategories, FaceCategory, propFaceCategories } from "building-types/materials/faceCategories";
import type { Prop, PropAttributeTable, PropWithAssetId } from "building-types/props/propTypes";
import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import { assertIsNotNull, assertIsNotUndefined } from "common/utils/assertOfType";
import { faceCategoryFromPropCategory } from "doc/propTools";
import { resolvePropAssetId } from "./resolvePropAssetId";

export function collectBuildingPropsFromRoofs(
  building: Pick<Building, "roofSystems">,
  propCatalog: PropAttributeTable,
  faceCategoryFilter: Set<FaceCategory> = new Set<FaceCategory>(allFaceCategories),
  propsOut: PropWithAssetId[] = [],
) {
  const addRoofSystemProps = (roof: GeneratedRoofSystem) => {
    const { roofs: subRoofs } = roof;
    for (const subRoofId in subRoofs) {
      const { props: roofProps, pickHandle } = subRoofs[subRoofId];
      if (roofProps) {
        const entityHandleForSelectingReportingUnit: DocEntityHandle = { type: "ROOF", id: subRoofId };
        propsOut.push(
          ...roofProps
            .map((prop) => {
              const propAttributes = propCatalog[prop.src];

              const faceCategory = faceCategoryFromPropCategory(propAttributes?.category);
              if (!faceCategoryFilter.has(faceCategory)) {
                return;
              }

              return {
                ...prop,
                faceCategory,
                pickHandle,
                levelId: roof.levelId,
                aboveLevel: true,
                assetId: resolvePropAssetId(prop, propCatalog),
                entityHandleForSelectingReportingUnit,
              };
            })
            .filter((prop) => prop !== undefined),
        );
      }
    }
  };

  const { roofSystems } = building;
  for (const roofId in roofSystems) {
    addRoofSystemProps(roofSystems[roofId]);
  }
}

export function collectBuildingRegularProps(
  building: Pick<Building, "props">,
  propCatalog: PropAttributeTable,
  faceCategoryFilter: Set<FaceCategory> = new Set<FaceCategory>(allFaceCategories),
  propsOut: PropWithAssetId[] = [],
) {
  for (const propId in building.props) {
    const prop: Prop = building.props[propId];
    assertIsNotNull(prop.room);

    const levelId = prop.room.level.id;
    const propAttributes = propCatalog[prop.src];
    if (!propAttributes) {
      continue;
    }
    const faceCategory = faceCategoryFromPropCategory(propAttributes.category);
    if (!faceCategoryFilter.has(faceCategory)) {
      continue;
    }
    propsOut.push({
      ...prop,
      labels: { ...propAttributes.labels, ...prop.labels }, // Prop labels are not yet resolved from the prop attributes for props generated by built-in room layouts.
      faceCategory,
      levelId,
      aboveLevel: false,
      assetId: resolvePropAssetId(prop, propCatalog),
      entityHandleForSelectingReportingUnit: roomEntityHandleForSelectingReportingUnit(prop.room),
    });
  }
}

function doorFaceCategory(unit: DoorUnit) {
  const isExterior = unit.exteriorBack.geom.length + unit.exteriorFront.geom.length > 0;
  if (isDoorUnitCurved(unit)) {
    return isExterior ? FaceCategory.DOOR_CURVED_WALL_EXTERIOR : FaceCategory.DOOR_CURVED_WALL_INTERIOR;
  }
  return isExterior ? FaceCategory.DOOR_EXTERIOR : FaceCategory.DOOR_INTERIOR;
}

export function collectBuildingProps(
  building: Pick<Building, "doorUnits" | "windowUnits" | "porches" | "props" | "roofSystems" | "settings">,
  faceCategoryFilter: Set<FaceCategory> = new Set<FaceCategory>(allFaceCategories),
): PropWithAssetId[] {
  const propCatalog = building.settings.props;

  const props: PropWithAssetId[] = [];

  collectBuildingPropsFromRoofs(building, propCatalog, faceCategoryFilter, props);

  if (
    faceCategoryFilter.has(FaceCategory.DOOR_EXTERIOR) ||
    faceCategoryFilter.has(FaceCategory.DOOR_INTERIOR) ||
    faceCategoryFilter.has(FaceCategory.DOOR_CURVED_WALL_EXTERIOR) ||
    faceCategoryFilter.has(FaceCategory.DOOR_CURVED_WALL_INTERIOR)
  ) {
    const { doorUnits } = building;
    for (const doorId in doorUnits) {
      const unit = doorUnits[doorId];
      const faceCategory = doorFaceCategory(unit);
      if (!faceCategoryFilter.has(faceCategory)) {
        continue;
      }
      const doorReportingUnitHandle = roomEntityHandleForSelectingReportingUnit(unit.doorPlacement.frontRoom);
      if (unit.prop) {
        const levelId = unit.doorPlacement.frontRoom?.level.id;
        assertIsNotUndefined(levelId);

        props.push({
          ...unit.prop,
          faceCategory,
          levelId,
          aboveLevel: false,
          assetId: resolvePropAssetId(unit.prop, propCatalog),
          entityHandleForSelectingReportingUnit: doorReportingUnitHandle,
        });
      }
      if (unit.props) {
        const levelId = unit.doorPlacement.frontRoom?.level.id;
        assertIsNotUndefined(levelId);

        props.push(
          ...unit.props.map((prop) => {
            return {
              ...prop,
              faceCategory,
              levelId,
              aboveLevel: false,
              assetId: resolvePropAssetId(prop, propCatalog),
              entityHandleForSelectingReportingUnit: doorReportingUnitHandle,
            };
          }),
        );
      }
    }
  }

  if (faceCategoryFilter.has(FaceCategory.WINDOW_DECORATION)) {
    const { windowUnits } = building;
    for (const windowId in windowUnits) {
      const unit = windowUnits[windowId];
      if (unit.props) {
        props.push(
          ...unit.props.map((prop) => {
            return {
              ...prop,
              faceCategory: FaceCategory.WINDOW_DECORATION,
              levelId: unit.windowPlacement.frontRoom?.level.id,
              aboveLevel: false,
              assetId: resolvePropAssetId(prop, propCatalog),
              entityHandleForSelectingReportingUnit: roomEntityHandleForSelectingReportingUnit(
                unit.windowPlacement.frontRoom,
              ),
            };
          }),
        );
      }
    }
  }

  if (propFaceCategories.some((v) => faceCategoryFilter.has(v))) {
    const { porches } = building;
    for (const porch of Object.values(porches)) {
      if (porch.props) {
        props.push(
          ...porch.props
            .map((prop) => {
              const propAttributes = propCatalog[prop.src];

              const faceCategory = faceCategoryFromPropCategory(propAttributes?.category);

              if (!faceCategoryFilter.has(faceCategory)) {
                return;
              }

              return {
                ...prop,
                faceCategory,
                levelId: porch.rooms[0].level.id,
                aboveLevel: false,
                assetId: resolvePropAssetId(prop, propCatalog),
                entityHandleForSelectingReportingUnit: roomEntityHandleForSelectingReportingUnit(porch.rooms[0]),
              };
            })
            .filter((prop) => prop !== undefined),
        );
      }
    }
  }

  collectBuildingRegularProps(building, propCatalog, faceCategoryFilter, props);

  return props;
}
